import { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezonePlugin from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useSnackbar } from 'notistack'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { useCompany } from 'contexts'
import { API } from 'core/requests'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezonePlugin)

type SurveyReminderKind = 'beheard' | 'pulse'

type SurveyReminder = {
  id: number | string
  action: string
  type: SurveyReminderKind
  send_at: string
  sent_at: string | null
  enabled: boolean
  locked: boolean
}

type Props = {
  open: boolean
  onClose: () => void
  survey: any
  surveyType: SurveyReminderKind
}

const reminderRoutes = {
  beheard: {
    list: 'surveyReminders',
    update: 'surveyUpdateReminders',
    label: 'BeHeard',
  },
  pulse: {
    list: 'pulseSurveyReminders',
    update: 'pulseSurveyUpdateReminders',
    label: 'Pulse',
  },
} as const

export function ReminderNotificationsIcon({ sx = {}, ...props }: any) {
  return (
    <Box
      component='span'
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        color: 'inherit',
        ...sx,
      }}
      {...props}
    >
      <NotificationsActiveIcon sx={{ fontSize: 21 }} />
      <AccessTimeIcon
        sx={{
          position: 'absolute',
          right: -2,
          bottom: -2,
          bgcolor: 'background.paper',
          borderRadius: '50%',
          fontSize: 11,
        }}
      />
    </Box>
  )
}

const getErrorMessage = (response: any) => {
  if (Array.isArray(response?.errors) && response.errors[0]) {
    return response.errors[0]
  }

  return response?.message ?? 'Unable to update reminder settings'
}

export default function SurveyReminderNotificationsDialog({
  open,
  onClose,
  survey,
  surveyType,
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { company } = useCompany()!

  const surveyId = survey?.id
  const timezone =
    company?.timezone ?? survey?.company?.timezone ?? dayjs.tz.guess()
  const queryKey = useMemo(
    () => ['surveyReminders', surveyType, surveyId],
    [surveyId, surveyType],
  )

  const [draftReminders, setDraftReminders] = useState<SurveyReminder[]>([])

  const getZonedDate = (value: string) => dayjs(value).tz(timezone)

  const remindersQuery = useQuery(
    queryKey,
    () =>
      API.base(reminderRoutes[surveyType].list, {
        method: 'GET',
        pathParams: { id: surveyId },
      }),
    {
      enabled: open && !!surveyId,
      refetchOnWindowFocus: false,
      onSuccess: (response: any) => {
        if (Array.isArray(response)) {
          setDraftReminders(response)
        } else if (response?.errors) {
          enqueueSnackbar(getErrorMessage(response), { variant: 'error' })
          setDraftReminders([])
        }
      },
      onError: () => {
        enqueueSnackbar('Unable to load reminder settings', {
          variant: 'error',
        })
      },
    },
  )

  const updateMutation = useMutation(
    (payload: { reminders: { id: number | string; enabled: boolean }[] }) =>
      API.base(reminderRoutes[surveyType].update, {
        method: 'PATCH',
        pathParams: { id: surveyId },
        body: payload,
      }),
    {
      onSuccess: (response: any) => {
        if (Array.isArray(response)) {
          queryClient.setQueryData(queryKey, response)
          setDraftReminders(response)
          enqueueSnackbar('Reminder settings updated', { variant: 'success' })
        } else {
          enqueueSnackbar(getErrorMessage(response), { variant: 'error' })
        }
      },
      onError: () => {
        enqueueSnackbar('Unable to update reminder settings', {
          variant: 'error',
        })
      },
    },
  )

  const reminders = useMemo(
    () => (Array.isArray(remindersQuery.data) ? remindersQuery.data : []),
    [remindersQuery.data],
  )

  useEffect(() => {
    if (Array.isArray(remindersQuery.data)) {
      setDraftReminders(remindersQuery.data)
    }
  }, [remindersQuery.data])

  useEffect(() => {
    if (!open) {
      setDraftReminders([])
    }
  }, [open])

  const originalEnabledById = useMemo(() => {
    const byId = new Map<string, boolean>()
    reminders.forEach((reminder: SurveyReminder) => {
      byId.set(String(reminder.id), reminder.enabled)
    })
    return byId
  }, [reminders])

  const changedReminders = useMemo(
    () =>
      draftReminders
        .filter(reminder => {
          if (reminder.locked) return false

          const originalEnabled = originalEnabledById.get(String(reminder.id))

          return (
            originalEnabled !== undefined &&
            originalEnabled !== reminder.enabled
          )
        })
        .map(reminder => ({
          id: reminder.id,
          enabled: reminder.enabled,
        })),
    [draftReminders, originalEnabledById],
  )

  const unlockedCount = draftReminders.filter(reminder => !reminder.locked)
    .length

  const surveyDateRange = useMemo(() => {
    const openDate = survey?.open_at
      ? getZonedDate(survey.open_at).format('MMM D, YYYY')
      : null
    const closeDate = survey?.close_at
      ? getZonedDate(survey.close_at).format('MMM D, YYYY')
      : null

    if (openDate && closeDate) return `${openDate} to ${closeDate}`
    if (openDate) return `Opens ${openDate}`
    if (closeDate) return `Closes ${closeDate}`

    return ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?.open_at, survey?.close_at, timezone])

  const handleToggle = (reminder: SurveyReminder) => {
    if (reminder.locked) return

    setDraftReminders(current =>
      current.map(item =>
        item.id === reminder.id ? { ...item, enabled: !item.enabled } : item,
      ),
    )
  }

  const handleBulkUpdate = (enabled: boolean) => {
    setDraftReminders(current =>
      current.map(reminder =>
        reminder.locked ? reminder : { ...reminder, enabled },
      ),
    )
  }

  const handleSave = () => {
    if (!changedReminders.length) return

    updateMutation.mutate({ reminders: changedReminders })
  }

  const isLoading = remindersQuery.isFetching && !draftReminders.length

  const renderSkeleton = () => (
    <Stack spacing={2}>
      {[0, 1, 2].map(item => (
        <Box key={item} sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant='circular' width={22} height={22} />
          <Skeleton
            variant='rectangular'
            height={96}
            sx={{ flex: 1, borderRadius: 2 }}
          />
        </Box>
      ))}
    </Stack>
  )

  const renderEmptyState = () => (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 320,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: '50%',
          color: theme.palette.primary.main,
          display: 'flex',
          height: 88,
          justifyContent: 'center',
          mb: 2,
          width: 88,
        }}
      >
        <NotificationsOffIcon sx={{ fontSize: 44 }} />
      </Box>
      <Typography variant='h6' color='text.primary' fontWeight={700}>
        No reminders yet
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }}>
        They appear once the survey is live.
      </Typography>
    </Box>
  )

  const renderReminder = (reminder: SurveyReminder, index: number) => {
    const locked = reminder.locked || !!reminder.sent_at
    const sendAt = getZonedDate(reminder.send_at)
    const sentAt = reminder.sent_at ? getZonedDate(reminder.sent_at) : null
    const enabled = !!reminder.enabled
    const isLast = index === draftReminders.length - 1
    const sentColor = theme.palette.secondary.main
    const disabledColor = theme.palette.grey[400]
    const nodeColor = locked
      ? sentColor
      : enabled
      ? theme.palette.primary.main
      : disabledColor
    const relativeLabel =
      locked && sentAt ? `Sent ${sentAt.fromNow()}` : sendAt.fromNow()
    const stateLabel = locked ? 'Sent' : enabled ? 'Enabled' : 'Paused'

    return (
      <Box
        key={reminder.id}
        sx={{
          display: 'grid',
          gridTemplateColumns: '32px 1fr',
          minHeight: isLast ? 'auto' : 102,
          position: 'relative',
        }}
      >
        {!isLast && (
          <Box
            sx={{
              bgcolor: alpha(theme.palette.text.secondary, 0.18),
              bottom: -6,
              left: 12,
              position: 'absolute',
              top: 26,
              width: 2,
            }}
          />
        )}
        <Box
          sx={{
            alignItems: 'center',
            bgcolor:
              locked || enabled ? nodeColor : theme.palette.background.paper,
            border: `2px solid ${nodeColor}`,
            borderRadius: '50%',
            display: 'flex',
            height: 26,
            justifyContent: 'center',
            mt: 1.75,
            position: 'relative',
            width: 26,
            zIndex: 1,
          }}
        >
          <Typography
            component='span'
            sx={{
              color: locked || enabled ? 'white' : theme.palette.text.secondary,
              fontSize: 12,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {index + 1}
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            bgcolor: locked
              ? alpha(sentColor, 0.08)
              : enabled
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.grey[400], 0.08),
            border: '1px solid',
            borderColor: locked
              ? alpha(sentColor, 0.34)
              : enabled
              ? alpha(theme.palette.primary.main, 0.24)
              : alpha(theme.palette.text.secondary, 0.14),
            borderRadius: 2,
            mb: 1.25,
            px: 2,
            py: 1.35,
            transition:
              'background-color 180ms ease, border-color 180ms ease, transform 180ms ease',
            '&:hover': {
              transform: locked ? 'none' : 'translateY(-1px)',
            },
          }}
        >
          <Stack direction='row' alignItems='flex-start' spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                flexWrap='wrap'
                useFlexGap
              >
                <Typography variant='subtitle1' fontWeight={700}>
                  {sendAt.format('ddd, MMM D [at] h:mm A')}
                </Typography>
                <Chip
                  label={relativeLabel}
                  size='small'
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.dark,
                    fontWeight: 600,
                  }}
                />
              </Stack>

              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mt: 1.25 }}
              >
                <Chip
                  icon={locked ? <CheckCircleIcon /> : undefined}
                  label={stateLabel}
                  size='small'
                  color={locked ? 'default' : enabled ? 'primary' : 'default'}
                  sx={{
                    fontWeight: 700,
                    ...(locked && {
                      bgcolor: alpha(sentColor, 0.18),
                      color: sentColor,
                      '& .MuiChip-icon': {
                        color: sentColor,
                      },
                    }),
                    ...(!locked &&
                      !enabled && {
                        bgcolor: alpha(theme.palette.grey[400], 0.22),
                        color: theme.palette.text.secondary,
                      }),
                  }}
                />
              </Stack>
            </Box>

            {!locked && (
              <Switch
                color='primary'
                checked={enabled}
                disabled={updateMutation.isLoading}
                onChange={() => handleToggle(reminder)}
                inputProps={{
                  'aria-label': `Reminder on ${sendAt.format(
                    'MMM D, YYYY h:mm A',
                  )}, ${enabled ? 'enabled' : 'disabled'}`,
                }}
              />
            )}
          </Stack>
        </Paper>
      </Box>
    )
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        zIndex: theme.zIndex.appBar + 2,
      }}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: isMobile ? 0 : 2,
          borderBottomLeftRadius: isMobile ? 0 : 2,
          maxWidth: '100%',
          width: { xs: '100%', sm: 560 },
        },
      }}
    >
      <Box
        role='dialog'
        aria-labelledby='reminder-notifications-title'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction='row' alignItems='flex-start' spacing={2}>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
                color: theme.palette.primary.main,
                display: 'flex',
                height: 48,
                justifyContent: 'center',
                width: 48,
              }}
            >
              <ReminderNotificationsIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                id='reminder-notifications-title'
                variant='h5'
                fontWeight={800}
              >
                Reminder notifications
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                Choose which reminders go out for this survey.
              </Typography>
            </Box>
            <IconButton aria-label='Close reminder notifications' onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              bgcolor: 'transparent',
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              borderRadius: 0,
              mt: 2.5,
              px: 1.5,
              py: 0.5,
            }}
          >
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={700}
              sx={{
                display: 'block',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
            >
              Survey
            </Typography>
            <Typography variant='h6' fontWeight={800} noWrap>
              {survey?.name ?? `${reminderRoutes[surveyType].label} survey`}
            </Typography>
            {surveyDateRange && (
              <Typography variant='body2' color='text.secondary'>
                {surveyDateRange}
              </Typography>
            )}
          </Paper>

          {draftReminders.length > 0 && (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              spacing={1}
              sx={{ mt: 2 }}
            >
              <Stack direction='row' spacing={1}>
                <Button
                  size='small'
                  onClick={() => handleBulkUpdate(true)}
                  disabled={!unlockedCount || updateMutation.isLoading}
                >
                  Enable all
                </Button>
                <Button
                  size='small'
                  onClick={() => handleBulkUpdate(false)}
                  disabled={!unlockedCount || updateMutation.isLoading}
                >
                  Disable all
                </Button>
              </Stack>
              <Chip
                size='small'
                label={`${changedReminders.length} changes`}
                sx={{
                  fontWeight: 700,
                  visibility: changedReminders.length ? 'visible' : 'hidden',
                }}
              />
            </Stack>
          )}
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2.5, sm: 3 } }}>
          {isLoading
            ? renderSkeleton()
            : draftReminders.length
            ? draftReminders.map(renderReminder)
            : renderEmptyState()}
        </Box>

        <Divider />

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1.5,
            justifyContent: 'space-between',
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            {changedReminders.length
              ? `${changedReminders.length} unsaved ${
                  changedReminders.length === 1 ? 'change' : 'changes'
                }`
              : 'No unsaved changes'}
          </Typography>

          <Stack direction='row' spacing={1}>
            <Button onClick={onClose} disabled={updateMutation.isLoading}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={!changedReminders.length || updateMutation.isLoading}
            >
              {updateMutation.isLoading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                'Save'
              )}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  )
}
